import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../context/ThemeContext';
import { getTheme } from '../../theme';
import MainLayout from '../../components/Layout/MainLayout';
import Button from '../../components/common/Button';
import { api } from '../../services/api';
import { CloudUpload, Refresh, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import AppVersionForm from './AppVersionForm';
import AppVersionUploadModal from './AppVersionUploadModal';
import AppVersionDetailsModal from './AppVersionDetailsModal';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.lg};
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.lg};
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  margin: 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: ${props => getTheme(props.theme?.isDark).colors.surface};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.lg};
  overflow: hidden;
  box-shadow: ${props => getTheme(props.theme?.isDark).shadows.sm};
`;

const TableHeader = styled.thead`
  background-color: ${props => getTheme(props.theme?.isDark).colors.surfaceElevated};
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  
  &:hover {
    background-color: ${props => getTheme(props.theme?.isDark).colors.hover};
  }
`;

const TableHeaderCell = styled.th`
  padding: ${props => getTheme(props.theme?.isDark).spacing.md};
  text-align: left;
  font-weight: 600;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
`;

const TableCell = styled.td`
  padding: ${props => getTheme(props.theme?.isDark).spacing.md};
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
`;

const PlatformBadge = styled(Badge)`
  background-color: ${props => props.platform === 'ios' ? '#007AFF' : '#3DDC84'};
  color: white;
`;

const StatusBadge = styled(Badge)`
  background-color: ${props => props.hasFile ? '#10B981' : '#EF4444'};
  color: white;
`;

const LambdaTestBadge = styled(Badge)`
  background-color: ${props => props.hasLambdaTest ? '#10B981' : '#6B7280'};
  color: white;
`;

const ActionButton = styled.button`
  padding: ${props => getTheme(props.theme?.isDark).spacing.xs} ${props => getTheme(props.theme?.isDark).spacing.sm};
  border: none;
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.primary};
  color: white;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  margin-right: ${props => getTheme(props.theme?.isDark).spacing.xs};
  
  &:hover {
    opacity: 0.9;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AppVersionsList = () => {
  const { isDark } = useTheme();
  const theme = { isDark };

  const [appVersions, setAppVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [uploadingToLambdaTest, setUploadingToLambdaTest] = useState({});

  useEffect(() => {
    fetchAppVersions();
  }, []);

  const fetchAppVersions = async () => {
    setLoading(true);
    try {
      const data = await api.getAppVersions();
      setAppVersions(data.results || []);
    } catch (err) {
      console.error('Error fetching app versions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (version) => {
    setSelectedVersion(version);
    setFormOpen(true);
  };

  const handleCreate = () => {
    setSelectedVersion(null);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedVersion(null);
    fetchAppVersions();
  };

  const handleUploadFile = (version) => {
    setSelectedVersion(version);
    setUploadModalOpen(true);
  };

  const handleUploadToLambdaTest = async (version) => {
    if (!version.appFilePath) {
      alert('Əvvəlcə app faylını yükləyin');
      return;
    }

    setUploadingToLambdaTest({ ...uploadingToLambdaTest, [version._id || version.id]: true });

    try {
      const result = await api.uploadAppVersionToLambdaTest(version._id || version.id, {
        appName: `Fit ${version.platform} ${version.latestVersion}`,
        customId: `fit-${version.platform}-${version.latestVersion}`,
        visibility: 'individual',
      });

      // Refresh app versions
      await fetchAppVersions();
      
      alert(`App uğurla LambdaTest-ə yükləndi! App ID: ${result.app_id}`);
    } catch (err) {
      alert('LambdaTest-ə yükləmə xətası: ' + (err.response?.data?.message || err.message));
    } finally {
      setUploadingToLambdaTest({ ...uploadingToLambdaTest, [version._id || version.id]: false });
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  return (
    <MainLayout>
      <PageContainer theme={theme}>
        <PageHeader theme={theme}>
          <Title theme={theme}>App Versions</Title>
          <div style={{ display: 'flex', gap: getTheme(isDark).spacing.sm }}>
            <Button
              onClick={handleCreate}
              variant="primary"
              theme={theme}
            >
              <CloudUpload /> Yeni Version
            </Button>
            <Button
              onClick={fetchAppVersions}
              variant="outline"
              theme={theme}
              disabled={loading}
            >
              <Refresh /> Yenilə
            </Button>
          </div>
        </PageHeader>

        <Table theme={theme}>
        <TableHeader theme={theme}>
          <TableRow theme={theme}>
            <TableHeaderCell theme={theme}>Platform</TableHeaderCell>
            <TableHeaderCell theme={theme}>Latest Version</TableHeaderCell>
            <TableHeaderCell theme={theme}>Min Required</TableHeaderCell>
            <TableHeaderCell theme={theme}>Force Update</TableHeaderCell>
            <TableHeaderCell theme={theme}>App File</TableHeaderCell>
            <TableHeaderCell theme={theme}>LambdaTest</TableHeaderCell>
            <TableHeaderCell theme={theme}>Actions</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <tbody>
          {loading ? (
            <TableRow theme={theme}>
              <TableCell colSpan="7" theme={theme} style={{ textAlign: 'center', padding: getTheme(isDark).spacing.xl }}>
                Yüklənir...
              </TableCell>
            </TableRow>
          ) : appVersions.length === 0 ? (
            <TableRow theme={theme}>
              <TableCell colSpan="7" theme={theme} style={{ textAlign: 'center', padding: getTheme(isDark).spacing.xl }}>
                App Version tapılmadı
              </TableCell>
            </TableRow>
          ) : (
            appVersions.map((version) => (
              <TableRow key={version._id || version.id} theme={theme}>
                <TableCell theme={theme}>
                  <PlatformBadge platform={version.platform} theme={theme}>
                    {version.platform}
                  </PlatformBadge>
                </TableCell>
                <TableCell theme={theme}>{version.latestVersion}</TableCell>
                <TableCell theme={theme}>{version.minRequiredVersion}</TableCell>
                <TableCell theme={theme}>
                  {version.forceUpdate ? (
                    <CheckCircle style={{ color: '#10B981' }} />
                  ) : (
                    <ErrorIcon style={{ color: '#6B7280' }} />
                  )}
                </TableCell>
                <TableCell theme={theme}>
                  <StatusBadge hasFile={!!version.appFilePath} theme={theme}>
                    {version.appFilePath ? 'Yüklənib' : 'Yoxdur'}
                  </StatusBadge>
                  {version.appFileName && (
                    <div style={{ fontSize: '12px', color: getTheme(isDark).colors.textSecondary, marginTop: '4px' }}>
                      {version.appFileName} ({formatFileSize(version.appFileSize)})
                    </div>
                  )}
                </TableCell>
                <TableCell theme={theme}>
                  <LambdaTestBadge hasLambdaTest={!!version.lambdatestAppId} theme={theme}>
                    {version.lambdatestAppId ? 'Yüklənib' : 'Yoxdur'}
                  </LambdaTestBadge>
                  {version.lambdatestAppId && (
                    <div style={{ fontSize: '12px', color: getTheme(isDark).colors.textSecondary, marginTop: '4px' }}>
                      {version.lambdatestAppId}
                    </div>
                  )}
                </TableCell>
                <TableCell theme={theme}>
                  <ActionButton theme={theme} onClick={() => {
                    setSelectedVersion(version);
                    setDetailsModalOpen(true);
                  }}>
                    Detallar
                  </ActionButton>
                  <ActionButton theme={theme} onClick={() => handleEdit(version)}>
                    Redaktə et
                  </ActionButton>
                  <ActionButton 
                    theme={theme} 
                    onClick={() => handleUploadFile(version)}
                    style={{ backgroundColor: getTheme(isDark).colors.primary }}
                  >
                    App Yüklə
                  </ActionButton>
                  {version.appFilePath && (
                    <ActionButton
                      theme={theme}
                      onClick={() => handleUploadToLambdaTest(version)}
                      disabled={uploadingToLambdaTest[version._id || version.id]}
                      style={{ backgroundColor: '#10B981' }}
                    >
                      {uploadingToLambdaTest[version._id || version.id] ? 'Yüklənir...' : 'LambdaTest-ə Yüklə'}
                    </ActionButton>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </tbody>
      </Table>

      {formOpen && (
        <AppVersionForm
          isOpen={formOpen}
          onClose={handleFormClose}
          version={selectedVersion}
        />
      )}

      {uploadModalOpen && selectedVersion && (
        <AppVersionUploadModal
          isOpen={uploadModalOpen}
          onClose={() => {
            setUploadModalOpen(false);
            setSelectedVersion(null);
          }}
          version={selectedVersion}
          onSuccess={() => {
            setUploadModalOpen(false);
            setSelectedVersion(null);
            fetchAppVersions();
          }}
        />
      )}

      {detailsModalOpen && selectedVersion && (
        <AppVersionDetailsModal
          isOpen={detailsModalOpen}
          onClose={() => {
            setDetailsModalOpen(false);
            setSelectedVersion(null);
          }}
          version={selectedVersion}
          onWhatsNewCreate={(version) => {
            // Open What's New form in new window or navigate
            window.open(`/admin/whats-new?appVersionId=${version._id || version.id}&platform=${version.platform}&version=${version.latestVersion}`, '_blank');
          }}
        />
      )}
      </PageContainer>
    </MainLayout>
  );
};

export default AppVersionsList;

